<script setup lang="ts">
import { computed, ref, watch } from "vue";
import TextAreaField from "@/design/primitives/TextAreaField.vue";
import { say } from "../say";
import type { WidgetProps } from "./contract";

/**
 * Free-form JSON.
 *
 * The only field type whose purpose IS data without a shape (I-F1). Everything
 * else gets a real column; this one is the explicit exception, so it earns a
 * plain editor rather than a form.
 *
 * The text is held locally while it is being typed: emitting a parsed value on
 * every keystroke would blow away what someone is halfway through writing the
 * moment it is briefly invalid.
 */
const props = defineProps<WidgetProps & { controlId?: string; describedBy?: string }>();
const emit = defineEmits<{ "update:modelValue": [unknown]; blur: [] }>();

const draft = ref("");

watch(
	() => props.modelValue,
	(value) => {
		const rendered =
			value === null || value === undefined
				? ""
				: typeof value === "string"
					? value
					: JSON.stringify(value, null, 2);
		// Only when the outside changed it: reformatting mid-edit moves the cursor.
		if (rendered !== draft.value && document.activeElement?.tagName !== "TEXTAREA") {
			draft.value = rendered;
		}
	},
	{ immediate: true },
);

const problem = computed(() => {
	if (draft.value.trim() === "") return "";
	try {
		JSON.parse(draft.value);
		return "";
	} catch {
		return say("invalidJson");
	}
});

function commit(): void {
	if (draft.value.trim() === "") {
		emit("update:modelValue", null);
	} else if (!problem.value) {
		emit("update:modelValue", JSON.parse(draft.value));
	} else {
		// O texto cru segue para o formulário: a validação `json` o recusa e bloqueia o salvar,
		// em vez de o registro gravar o valor antigo (vazio) calado (BUG-02).
		emit("update:modelValue", draft.value);
	}
	emit("blur");
}
</script>

<template>
	<!--
		A recusa é DESTE widget, não do formulário: só ele sabe que o texto ainda
		não fecha como JSON enquanto está sendo escrito.
	-->
	<TextAreaField
		:id="controlId"
		v-model="draft"
		:rows="6"
		spellcheck="false"
		:disabled="disabled"
		:error="error ? undefined : problem"
		:invalid="Boolean(error) || Boolean(problem)"
		:described-by="describedBy"
		class="font-mono text-xs"
		@blur="commit"
	/>
</template>
